import { useRef } from 'react';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from '@frontend/i18n';
import {
  AlertIcon,
  Button,
  ImageIcon,
  NakedButton,
  PlusIconSmall,
  SpinningLoader,
  Text,
} from '@frontend/design-system';
import { formatFieldLabel } from '../../../../../../helpers';
import type { DynamicFormApi } from '../../hooks/use-app-form';
import {
  IMAGE_UPLOAD_RULES,
  MediaAttachment,
  MediaValidationRules,
  getMediaFieldValidator,
  useMediaFieldUpload,
} from '../../media-upload';
import { useNonReactiveDynamicFormStore } from '../../store';
import type { DynamicFormField } from '../../types';
import {
  addImageButtonStyle,
  addMoreButtonStyle,
  descTextStyle,
  emptyStateStyle,
  fieldContainerStyle,
  fieldErrorStyle,
  fieldLabelStyle,
  fieldsetStyle,
  imageCollectionItemStyle,
  imageCollectionStyle,
  overlayErrorIconStyle,
  overlayInlineTextStyle,
  removeIconButtonStyle,
  retryButtonStyle,
  uploadStatusOverlayErrorStyle,
  uploadStatusOverlayStyle,
  uploadTextStyle,
} from './image-upload-field.styles';

const MAX_IMAGE_UPLOADS = 5;

export interface ImageUploadFieldProps {
  form: DynamicFormApi;
  fieldInfo: DynamicFormField;
}

export const ImageUploadField = ({ form, fieldInfo }: ImageUploadFieldProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const store = useNonReactiveDynamicFormStore();
  const required = !!fieldInfo.required;
  const maxFiles = Number(fieldInfo.fieldMeta.limit) || MAX_IMAGE_UPLOADS;

  const rules: MediaValidationRules = { ...IMAGE_UPLOAD_RULES, maxFiles, required };
  const validator = getMediaFieldValidator({ fieldKey: fieldInfo.key, rules, t, getState: store.getState });

  const fieldErrors = useStore(form.store, (state) => {
    const meta = state.fieldMeta[fieldInfo.key];
    if (!meta?.isTouched) return [];
    return (meta?.errors ?? []) as string[];
  });

  return (
    <fieldset css={fieldsetStyle} role='group' aria-required={required} aria-labelledby={`${fieldInfo.key}-label`}>
      <Text id={`${fieldInfo.key}-label`} css={fieldLabelStyle}>
        {formatFieldLabel(fieldInfo.label, fieldInfo.required)}
      </Text>

      <div css={fieldContainerStyle}>
        <form.AppField name={fieldInfo.key} validators={{ onChange: () => validator(), onSubmit: () => validator() }}>
          {(field) => (
            <ImageUploadInput
              fieldKey={fieldInfo.key}
              rules={rules}
              hasError={fieldErrors.length > 0}
              onChange={(mediaIds) => field.handleChange(mediaIds)}
            />
          )}
        </form.AppField>
      </div>

      {fieldErrors.length > 0 && (
        <Text size='small' css={fieldErrorStyle}>
          {fieldErrors.join(', ')}
        </Text>
      )}
    </fieldset>
  );
};

interface ImageUploadInputProps {
  fieldKey: string;
  rules: MediaValidationRules;
  hasError: boolean;
  onChange: (mediaIds: string[]) => void;
}

const ImageUploadInput = ({ fieldKey, rules, hasError, onChange }: ImageUploadInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { attachments, addFiles, remove, retry } = useMediaFieldUpload({ fieldKey, rules, onChange });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  const selectFile = () => fileInputRef.current?.click();
  const maxFiles = rules.maxFiles ?? MAX_IMAGE_UPLOADS;

  return (
    <div id={fieldKey} aria-invalid={hasError} tabIndex={-1}>
      {attachments.length === 0 ? (
        <EmptyState selectFile={selectFile} limit={maxFiles} />
      ) : (
        <ImageCollection
          attachments={attachments}
          selectFile={selectFile}
          onRemove={(id) => remove(id)}
          onRetry={(id) => retry(id)}
          limit={maxFiles}
        />
      )}
      <input ref={fileInputRef} type='file' accept='image/*' multiple hidden onChange={handleFileChange} />
    </div>
  );
};

interface EmptyStateProps {
  selectFile: () => void;
  limit: number;
}

const EmptyState = ({ selectFile, limit }: EmptyStateProps) => {
  const { t } = useTranslation('digital-forms-experience');

  return (
    <div css={emptyStateStyle}>
      <ImageIcon color='subdued' />
      <Text size='small' weight='bold' textAlign='center' css={uploadTextStyle}>
        {t('Upload Image')}
      </Text>
      <Text size='small' color='light' textAlign='center' css={descTextStyle}>
        {t('Max file size: 12MB')}
        <br />
        {t('Max {{limit}} images allowed', { limit })}
      </Text>
      <Button variant='secondary' type='button' onClick={selectFile} css={addImageButtonStyle}>
        {t('Add Image')}
      </Button>
    </div>
  );
};

interface ImageCollectionProps {
  attachments: MediaAttachment[];
  limit: number;
  selectFile: () => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

const ImageCollection = ({ attachments, limit, selectFile, onRemove, onRetry }: ImageCollectionProps) => {
  const { t } = useTranslation('digital-forms-experience');

  return (
    <div css={imageCollectionStyle}>
      {attachments.map((attachment) => (
        <ImageCollectionItem
          key={attachment.id}
          attachment={attachment}
          onRemove={() => onRemove(attachment.id)}
          onRetry={() => onRetry(attachment.id)}
        />
      ))}
      {attachments.length < limit && (
        <div css={imageCollectionItemStyle}>
          <NakedButton onClick={selectFile} css={addMoreButtonStyle}>
            <PlusIconSmall color='primary' />
            <Text size='small' weight='bold' color='primary'>
              {t('Add More')}
            </Text>
          </NakedButton>
        </div>
      )}
    </div>
  );
};

interface ImageCollectionItemProps {
  attachment: MediaAttachment;
  onRemove: () => void;
  onRetry: () => void;
}

const ImageCollectionItem = ({ attachment, onRemove, onRetry }: ImageCollectionItemProps) => {
  const { t } = useTranslation('digital-forms-experience');
  const isUploading = attachment.status === 'uploading';
  const hasError = attachment.status === 'error';

  return (
    <div css={imageCollectionItemStyle}>
      <img src={attachment.previewUrl} alt='' />

      {isUploading && (
        <div css={uploadStatusOverlayStyle}>
          <SpinningLoader size='xs' />
          <Text size='small' css={overlayInlineTextStyle}>
            {t('Uploading...')}
          </Text>
        </div>
      )}

      {hasError && (
        <div css={uploadStatusOverlayErrorStyle}>
          <AlertIcon css={overlayErrorIconStyle} />
          <Text size='medium' css={overlayInlineTextStyle}>
            {t('Upload failed')}
          </Text>
          <NakedButton onClick={onRetry} css={retryButtonStyle}>
            {t('Retry')}
          </NakedButton>
        </div>
      )}

      <Button
        variant='tertiary'
        size='tiny'
        iconName='x-small'
        css={removeIconButtonStyle}
        hoverLabel={t('Remove image')}
        onClick={onRemove}
      />
    </div>
  );
};
